import classNames from 'classnames';
import React, { useMemo } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Link from '@/components/utilities/link/link';
import { MainNavigationMenuItem } from '../../main-navigation';
import styles from './main-navigation-large-menu-item.module.scss';

export interface MainNavigationLargeMenuItemProps {
  /** The class name to apply */
  className?: string;
  /** The number to show as a count */
  count?: number;
  /** Whether to show the menu count */
  hideNumbers?: boolean;
  /** The icon to show */
  icon?: IconName;
  /** Indicates whether the menu item is active */
  isActive?: boolean;
  /** Whether this item should open a dropdown instead of navigating */
  isDropdownTrigger?: boolean;
  /** Indicates whether this is a featured item */
  isFeatured?: boolean;
  /** Indicates whether this is a priority item */
  isPriority?: boolean;
  /** Indicates whether this is a quick menu item */
  isQuickMenu?: boolean;
  /** Indicates whether the component is on a transparent navigation */
  isTransparent?: boolean;
  /** The list of items to show */
  items?: MainNavigationMenuItem[];
  /** The link to show */
  link: Content.Properties.Link;
  /** An event that is triggered on click */
  onClick?: (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  /** The type of the menu item */
  type?: MainNavigationMenuItem['type'];
}

const MainNavigationLargeMenuItem = ({
  className,
  count,
  icon,
  hideNumbers = false,
  isActive = false,
  isDropdownTrigger = false,
  isFeatured = false,
  isPriority = false,
  isQuickMenu = false,
  isTransparent = false,
  items = [],
  link,
  onClick,
  type = 'default',
}: MainNavigationLargeMenuItemProps) => {
  const iconName = useMemo(() => {
    if (items.length > 0) {
      return 'ChevronDown';
    }

    if (icon) {
      return icon;
    }
  }, [icon, items]);

  const countNumber = useMemo(() => {
    if (items.length > 0) {
      return items.reduce(
        (subItemCount, item) => (subItemCount += item?.items?.length || 1),
        0,
      );
    }

    if (count) {
      return count;
    }
  }, [count, items]);

  const content = (
    <React.Fragment>
      <div className={styles.text}>
        <Typography tagStyle="menuItem">{link.text}</Typography>
      </div>

      {(countNumber || iconName) && (
        <div className={styles.info}>
          {countNumber && !hideNumbers && (
            <Typography className={styles.count} tagStyle="menuItem">
              {countNumber}
            </Typography>
          )}

          {iconName && (
            // Keyed by the count so the pulse replays whenever it changes
            <div
              key={countNumber}
              className={classNames(styles.controls, {
                [styles.isPulsing]: countNumber,
              })}
            >
              <Icon className={styles.icon} name={iconName} size={14} />
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );

  const classNameProps = classNames(styles.component, className, {
    [styles.isActive]: isActive && type === 'default',
    [styles.isQuickMenu]: isQuickMenu,
    [styles.isSecondary]: isPriority,
    [styles.isTertiary]: isFeatured,
    [styles.isTransparent]: isTransparent,
  });

  if (isDropdownTrigger) {
    return (
      <button
        aria-expanded={isActive}
        className={classNameProps}
        onClick={onClick}
        type="button"
      >
        {content}
      </button>
    );
  }

  return (
    <Link className={classNameProps} onClick={onClick} url={link.url}>
      {content}
    </Link>
  );
};

MainNavigationLargeMenuItem.displayName = 'MainNavigationLargeMenuItem';

export default MainNavigationLargeMenuItem;
